/* eslint-disable */
/*
 * GroupedRadio
 *
 * @version 1.0.0
 * @author Patrizia Petrera
 * @since 04-Jul-2025
*/

import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { FormControl, FormGroup, FormControlLabel, Radio, RadioGroup } from '@mui/material'
import PortalTooltip from '@nexidigital/portaltooltip'
import './GroupedRadio.scss'

const GroupedRadio = ({
	invert,
	options,
	optionsGrouped,
	optionsGroupedLabels,
	defaultValue,
	onChange,
	row,
	grid,
	centered,
	withTiles,
	withBorders,
	error,
	groupError,
	groupErrorMessage,
	disableSendAnalytics,
	onFocus,
	onBlur,
	tabIndex,
	labelTooltip,
	labelIcon,
	onClickTooltip,
	textTooltip,
	infoToShow,
	...props
}) => {
	const theme = invert ? 'invert' : ''

	// Crea una mappa id->categoria usando optionsGrouped
	const idToCategory = {}
	const categoryKeys = optionsGrouped ? Object.keys(optionsGrouped) : []
	if (optionsGrouped) {
		Object.entries(optionsGrouped).forEach(([category, methods]) => {
			methods.forEach(method => {
				idToCategory[method.id] = category
			})
		})
	}

	// Raggruppa le options in base alle categorie dinamiche
	const grouped = categoryKeys.reduce((acc, key) => {
		acc[key] = []
		return acc
	}, {})

	if (options) {
		options.forEach(option => {
			const category = idToCategory[option.id]
			if (category && grouped[category]) {
				grouped[category].push(option)
			}
		})
	}

	const [selectedValue, setSelectedValue] = useState(
		defaultValue || (options && options[0] ? options[0].value : '')
	)

	useEffect(() => {
		if (defaultValue !== undefined) setSelectedValue(defaultValue)
	}, [defaultValue])

	// Analytics
	const dispatchAnalyticsEvent = (event, componentLabel) => {
		const globalEvent = new CustomEvent('focusInAndOutAnalytics', { detail: { label: componentLabel, event } })
		window.dispatchEvent(globalEvent)
	}

	const onFocusHandler = (e, inputLabel) => {
		!disableSendAnalytics && dispatchAnalyticsEvent(e, inputLabel)
		onFocus && onFocus(e)
	}

	const onBlurHandler = (e, inputLabel) => {
		!disableSendAnalytics && dispatchAnalyticsEvent(e, inputLabel)
		onBlur && onBlur(e)
	}

	const handleChange = event => {
		const { value } = event.target
		setSelectedValue(value)
		onChange && onChange(value, event)
	}

	return (
		<FormControl className='portalRadioBox_form'>
			<fieldset>
				<div>
					{labelTooltip && (
						<div className='labelTooltip'>
							{labelTooltip}
							{labelIcon && (
								<PortalTooltip
									onClick={onClickTooltip}
									text={textTooltip}
									infoToShow={infoToShow}
									size='xs'
								/>
							)}
						</div>
					)}
				</div>
				<FormGroup
					className={`portalRadioBox_group ${theme} ${!row ? 'column' : ''} ${row ? 'row' : ''} ${grid ? 'grid' : ''}`}
					row={row}
				>
					{Object.entries(grouped).map(([category, methods]) =>
						methods.length > 0
							? (
								<div key={category} className='groupedRadioRow'>
									{optionsGroupedLabels?.[category] !== '' && (
										<h2 className='categoryTitle'>{optionsGroupedLabels?.[category] || category}</h2>
									)}
									<RadioGroup
										name={category}
										value={selectedValue}
										onChange={handleChange}
										row={row}
										className='groupList'
									>
										{methods.map((option, index) => (
											<div
												key={option.id || index}
												className={`portalRadioBox_radio ${theme} ${withTiles || centered ? 'centered' : ''} ${withBorders ? 'withBorders' : ''} ${option.disabled ? 'disabled' : ''} ${selectedValue === option.value ? 'checked' : ''}`}
											>
												<FormControlLabel
													className={`controls ${withTiles ? 'withTiles' : ''} ${option.error || groupError ? 'error' : ''} ${withBorders && option.disabled ? 'bordersDisabled' : ''}`}
													value={option.value}
													control={
														<Radio
															id={option.id || index}
															disableRipple
															onFocus={e => onFocusHandler(e, option.label)}
															onBlur={e => onBlurHandler(e, option.label)}
															tabIndex={withTiles ? -1 : 0 || tabIndex}
															disabled={option.disabled}
														/>
													}
													label={
														<>
															{option.label}
															{option.subText && <div className='radioSubtext'>{option.subText}</div>}
															{option.childrenColumn && selectedValue === option.value
																? (
																	<div className='radioChildrenColumn'>{option.childrenColumn}</div>
																)
																: null}
														</>
													}
													disabled={option.disabled}
													required={option.required}
													tabIndex={option.disabled ? -1 : withTiles ? 0 : -1 || tabIndex}
													onKeyDown={e => {
														if ((e.key === ' ' || e.key === 'Enter') && !option.disabled) {
															e.preventDefault()
															document.getElementById(option.id || index)?.click()
														}
													}}
												/>
												{option.children && selectedValue === option.value
													? (
														<div className='radioChildren'>{option.children}</div>
													)
													: null}
												{option.externalParagraph && (
													<p className={option.externalParagraphStyle || 'paragraphStyle'}>{option.externalParagraph}</p>
												)}
											</div>
										))}
									</RadioGroup>
								</div>
							)
							: null
					)}
				</FormGroup>
				{groupErrorMessage && (
					<div className='errorMessage'>
						{groupErrorMessage}
					</div>
				)}
			</fieldset>
		</FormControl>
	)
}

GroupedRadio.propTypes = {
	options: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string,
			value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
			subText: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
			required: PropTypes.bool,
			disabled: PropTypes.bool,
			error: PropTypes.bool,
			checked: PropTypes.bool,
			selected: PropTypes.bool,
			indeterminate: PropTypes.bool,
			tabIndex: PropTypes.number,
			children: PropTypes.element,
			childrenColumn: PropTypes.element,
			externalParagraph: PropTypes.string,
			externalParagraphStyle: PropTypes.string
		})
	).isRequired,
	optionsGrouped: PropTypes.object,
	optionsGroupedLabels: PropTypes.object,
	defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	onChange: PropTypes.func,
	invert: PropTypes.bool,
	row: PropTypes.bool,
	grid: PropTypes.bool,
	centered: PropTypes.bool,
	withTiles: PropTypes.bool,
	withBorders: PropTypes.bool,
	error: PropTypes.bool,
	groupError: PropTypes.bool,
	groupErrorMessage: PropTypes.string,
	disableSendAnalytics: PropTypes.bool,
	onFocus: PropTypes.func,
	onBlur: PropTypes.func,
	tabIndex: PropTypes.number,
	labelTooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	labelIcon: PropTypes.bool,
	onClickTooltip: PropTypes.func,
	textTooltip: PropTypes.string,
	infoToShow: PropTypes.array
}

GroupedRadio.defaultProps = {
	options: [],
	optionsGrouped: {},
	optionsGroupedLabels: {},
	defaultValue: '',
	onChange: undefined,
	invert: false,
	row: false,
	grid: false,
	centered: false,
	withTiles: false,
	withBorders: false,
	error: false,
	groupError: false,
	groupErrorMessage: '',
	disableSendAnalytics: false,
	onFocus: undefined,
	onBlur: undefined,
	tabIndex: 0,
	labelTooltip: '',
	labelIcon: false,
	onClickTooltip: undefined,
	textTooltip: '',
	infoToShow: null
}

export default GroupedRadio
